import React from 'react';
import { MMLogoMark } from '@/components/brand/mm-logo';
import { cn } from '@/lib/utils';

/**
 * The sign-in page's brand moment: the bee works. It flies out to a cookie,
 * carries it home, drops it into a comb cell, and that cell fills - six cells,
 * six trips, then the hive empties and the loop starts again.
 *
 * It is the product's own story in eight seconds: signals are collected from out
 * there, carried back, and stored in the hive. Which is why it earns a place on
 * a login page at all.
 *
 * Four rules this file exists to hold:
 *
 *   1. The hive is a MOTIF, never a wallpaper. A full-bleed comb behind a login
 *      form fights the form - the eye lands on pattern instead of the email
 *      field. One masked cluster in the top-right dead space.
 *   2. Motion is ambient, not attention-seeking. Nothing flashes, nothing
 *      bounces; the bee moves at the speed of a real one hovering. A person
 *      arriving here locked out must never feel hurried by the page.
 *   3. Everything stops under prefers-reduced-motion - and then the comb is
 *      drawn already FULL, so the still frame still says "stored", not "empty".
 *   4. It is pure CSS on inline SVG: no library, no canvas, no JS timers. A
 *      login page must not carry an animation runtime, and a rAF loop left
 *      running on a backgrounded tab is a battery bug nobody attributes to us.
 */

const AMBER = '#E09400';
const HONEY = '#F5C451';
// The cookie was nearly brown at 22px and read as a pebble. Light biscuit with
// soft chips - it has to say "cookie" at a glance, from the corner of the eye.
const COOKIE = '#F3D9A8';
const COOKIE_EDGE = '#E3BF84';
const CHIP = '#C08A52';

// One trip per cell. At 13s each trip is ~2.2s - out, pick up, carry back - which
// reads as a bee doing a job. The earlier 8s was closer to darting.
const TRIPS = 6;
const CYCLE = 13;

/*
 * The route. Each cell gets a cookie parked outside the comb (left and below,
 * where the page is empty) and a home position at the cell's centre. The bee
 * shuttles between them; the cookie rides along on the second half.
 *
 * Coordinates are in the motif's own 300x230 viewBox, so the whole thing scales
 * as one piece and nothing needs to know about page pixels.
 */
const S3 = Math.sqrt(3);
const R = 30;
const CELLS = [];
for (let row = 0; row < 2; row += 1) {
  for (let col = 0; col < 3; col += 1) {
    CELLS.push({
      cx: 156 + col * S3 * R + (row ? (S3 * R) / 2 : 0),
      cy: 62 + row * 1.5 * R,
    });
  }
}
// Where each cookie waits: a loose scatter BELOW the comb, so the bee shuttles
// straight up into the hive and the whole story sits in one column of the page.
// Ordered so consecutive trips do not start from the same spot.
const COOKIES = [
  { x: 168, y: 182 }, { x: 246, y: 200 }, { x: 200, y: 208 },
  { x: 286, y: 178 }, { x: 150, y: 206 }, { x: 232, y: 176 },
];

function hexPath(cx, cy, r) {
  const pts = [];
  for (let k = 0; k < 6; k += 1) {
    const a = ((90 + 60 * k) * Math.PI) / 180;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)} ${(cy - r * Math.sin(a)).toFixed(1)}`);
  }
  return `M${pts.join(' L')} Z`;
}

/*
 * Per-trip keyframes, generated. Each trip owns a 1/6 slice of the cycle:
 *   first half  - bee travels out to the cookie (empty-handed)
 *   second half - bee travels home (cookie rides with it)
 * Outside its slice the bee for that trip is invisible, so exactly one bee is
 * ever on screen. Hand-writing 6 x 3 keyframe blocks would drift; this cannot.
 */
function buildKeyframes() {
  let css = '';
  CELLS.forEach((cell, i) => {
    const c = COOKIES[i];
    // The six trips occupy 0-96% of the cycle, not 0-100%. Two reasons, both
    // found the hard way: the last trip's delivery keyframe used to sit at
    // exactly 100%, where the loop-reset keyframe also lives - same offset, last
    // declaration wins, so the SIXTH CELL NEVER VISIBLY FILLED. And the spare
    // 4% now gives the full hive a held beat before it empties, which is the
    // moment the whole loop is for.
    const SPAN = 96;
    const t0 = (i / TRIPS) * SPAN;          // trip starts
    const tMid = ((i + 0.5) / TRIPS) * SPAN; // at the cookie
    const t1 = ((i + 1) / TRIPS) * SPAN;     // home, cookie delivered
    const out = (t1 - t0) * 0.06;           // fade in/out margin

    // The bee: home -> cookie -> home. translate only; the wing beat and the
    // body tilt live on inner elements so they compose instead of fighting.
    css += `
@keyframes lb-fly-${i} {
  0%, ${(t0).toFixed(2)}% { transform: translate(${cell.cx}px, ${cell.cy}px) scale(1.32); opacity: 0 }
  ${(t0 + out).toFixed(2)}% { opacity: 1 }
  ${(tMid).toFixed(2)}% { transform: translate(${c.x}px, ${c.y}px) scale(1.5); opacity: 1 }
  ${(t1 - out).toFixed(2)}% { transform: translate(${cell.cx}px, ${cell.cy}px) scale(1.32); opacity: 1 }
  ${(t1).toFixed(2)}%, 100% { transform: translate(${cell.cx}px, ${cell.cy}px) scale(1.32); opacity: 0 }
}`;

    // The cookie: sits waiting, vanishes the instant the bee reaches it (it is
    // now being carried - the carried copy rides on the bee itself), and stays
    // gone until the loop restarts.
    css += `
@keyframes lb-cookie-${i} {
  0%, ${(tMid - 0.4).toFixed(2)}% { opacity: .95; transform: scale(1) }
  ${(tMid).toFixed(2)}% { opacity: 0; transform: scale(.6) }
  ${(t1).toFixed(2)}%, 100% { opacity: 0; transform: scale(.6) }
}`;

    // The carried cookie: only visible on the way home, tucked under the bee.
    css += `
@keyframes lb-carry-${i} {
  0%, ${(tMid).toFixed(2)}% { opacity: 0 }
  ${(tMid + 0.5).toFixed(2)}% { opacity: 1 }
  ${(t1 - out * 1.4).toFixed(2)}% { opacity: 1 }
  ${(t1).toFixed(2)}%, 100% { opacity: 0 }
}`;

    // The cell fills as the cookie lands and holds until the cycle restarts -
    // the hive visibly accumulating is the whole point of the loop.
    css += `
@keyframes lb-fill-${i} {
  0%, ${(t1 - out * 1.6).toFixed(2)}% { opacity: 0; transform: scale(.55) }
  ${(t1).toFixed(2)}% { opacity: 1; transform: scale(1) }
  99.6% { opacity: 1; transform: scale(1) }
  100% { opacity: 0; transform: scale(.55) }
}`;
  });
  return css;
}

const CSS = `
.lb-wrap { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
/* Inline: the same bees at work, sized by the element they sit in - a banner,
   an attention band, the corner of an empty state. */
.lb-wrap.lb-inline { position: relative; inset: auto; display: block; overflow: visible; }
.lb-wrap.lb-inline .lb-svg { position: static; width: 100%; height: auto; }
/* 300x230 viewBox at 1.42x. The bee is ~41 viewBox units tall and flies at
   scale 1.5, so it lands at ~87px on screen - the Action Report's 96px, which
   is the size that was asked for. At the previous 340px it rendered ~45px and
   read as a speck. */
.lb-svg { position: absolute; top: 4%; right: 2%; width: 426px; height: 327px; }
.lb-cell { fill: none; stroke: ${AMBER}; stroke-width: 1.6; stroke-linejoin: round; opacity: .3 }
.lb-fill { fill: ${HONEY}; opacity: 0; fill-opacity: .58; transform-box: fill-box; transform-origin: 50% 50% }
.lb-cookie, .lb-carry { transform-box: fill-box; transform-origin: 50% 50% }
.lb-carry { opacity: 0 }
.lb-bee-g { opacity: 0 }

/* The card is centred at max-w-md; past ~1100px the motif and the card start
   sharing pixels, and a bee behind an input beats no bee only in a mockup. */
@media (max-width: 1100px) { .lb-wrap:not(.lb-inline) { display: none } }

/* Reduced motion: no flight, and the comb is shown already full - the still
   frame has to say "stored", which an empty comb would not. */
@media (prefers-reduced-motion: reduce) {
  /* No animation runs, so the parts need their resting placement explicitly -
     otherwise the wings and antennae sit at the untranslated origin. */
  .lb-wing-l { transform: translate(-2px, -6px) rotate(-6deg) }
  .lb-wing-r { transform: translate(2px, -6px) rotate(6deg) }
  .lb-ant-l  { transform: translate(-2.5px, -18.8px) }
  .lb-ant-r  { transform: translate(2.5px, -18.8px) }
  .lb-cookie, .lb-carry { opacity: 0 !important }
  .lb-fill { opacity: .85 !important; transform: none !important }
  .lb-bee-g.lb-rest { opacity: 1 !important }
}

@media (prefers-reduced-motion: no-preference) {
  .lb-bee-g { animation: lb-fly-0 ${CYCLE}s linear infinite }
  .lb-bee-g.lb-rest { display: none }

  /* Articulation. Every keyframe carries its OWN translate, so the pivot is the
     part's root and no transform-origin / transform-box is involved.
     That is not fussiness: a CSS \`transform\` REPLACES an element's SVG
     transform attribute, so the previous version's wings lost their -34deg
     placement the moment the animation applied and snapped flat instead of
     flapping. Placement now lives in the keyframes, and the static rotate stays
     on the child ellipse where the animation cannot touch it. */
  .lb-wing-l { animation: lb-wing-l .15s ease-in-out infinite alternate }
  .lb-wing-r { animation: lb-wing-r .15s ease-in-out infinite alternate; animation-delay: .075s }
  @keyframes lb-wing-l {
    from { transform: translate(-2px, -6px) rotate(8deg) }
    to   { transform: translate(-2px, -6px) rotate(-24deg) }
  }
  @keyframes lb-wing-r {
    from { transform: translate(2px, -6px) rotate(-8deg) }
    to   { transform: translate(2px, -6px) rotate(24deg) }
  }
  /* Antennae: a slow feel-the-air sweep, offset from each other so the pair
     reads as sensing rather than as windscreen wipers. */
  .lb-ant-l { animation: lb-ant-l 1.9s ease-in-out infinite }
  .lb-ant-r { animation: lb-ant-r 1.9s ease-in-out infinite; animation-delay: .3s }
  @keyframes lb-ant-l {
    0%, 100% { transform: translate(-2.5px, -18.8px) rotate(0deg) }
    35%      { transform: translate(-2.5px, -18.8px) rotate(-15deg) }
    70%      { transform: translate(-2.5px, -18.8px) rotate(7deg) }
  }
  @keyframes lb-ant-r {
    0%, 100% { transform: translate(2.5px, -18.8px) rotate(0deg) }
    35%      { transform: translate(2.5px, -18.8px) rotate(15deg) }
    70%      { transform: translate(2.5px, -18.8px) rotate(-7deg) }
  }
  /* The body bobs on the wingbeat - without it the bee reads as a sprite being
     dragged along a path, which is exactly what it looked like. */
  .lb-body { animation: lb-bob .26s ease-in-out infinite alternate }
  @keyframes lb-bob {
    from { transform: translateY(0) rotate(-1deg) }
    to   { transform: translateY(1.4px) rotate(1.5deg) }
  }
  ${Array.from({ length: TRIPS }, (_, i) => `
  .lb-bee-${i} { animation: lb-fly-${i} ${CYCLE}s linear infinite }
  .lb-cookie-${i} { animation: lb-cookie-${i} ${CYCLE}s linear infinite }
  .lb-carry-${i} { animation: lb-carry-${i} ${CYCLE}s linear infinite }
  .lb-fill-${i} { animation: lb-fill-${i} ${CYCLE}s linear infinite }`).join('')}
  ${buildKeyframes()}
}
`;

/** The cookie: a honey disc with bites out of it, small enough to read at 18px. */
function Cookie({ className, r = 11 }) {
  return (
    <g className={className}>
      <circle r={r} fill={COOKIE_EDGE} />
      <circle r={r * 0.84} fill={COOKIE} />
      <circle cx={-r * 0.3} cy={-r * 0.25} r={r * 0.15} fill={CHIP} />
      <circle cx={r * 0.32} cy={r * 0.05} r={r * 0.13} fill={CHIP} />
      <circle cx={-r * 0.05} cy={r * 0.4} r={r * 0.12} fill={CHIP} />
      <circle cx={r * 0.15} cy={-r * 0.45} r={r * 0.1} fill={CHIP} />
    </g>
  );
}

/** The bee: the brand's own (components/brand/mm-logo), never a redrawing
 * (owner, 23 Oct 2026: one logo, one bee). Centred on its origin; the body
 * group keeps its gentle bob, the mark keeps its own wings and antennae. */
function Bee({ carry }) {
  return (
    <g>
      <g className="lb-body">
        <g transform="translate(-15 -17)"><MMLogoMark size={30} tone="light" /></g>
      </g>
      {carry != null && <Cookie className={`lb-carry lb-carry-${carry}`} r={8.5} />}
    </g>
  );
}

/**
 * @param {'light'|'dark'} tone  only shifts the comb's weight - amber on white
 *   and amber on near-black need different opacities to read equally faint.
 */
export function LoginBee({ tone = 'light', className, inline = false }) {
  return (
    <div className={cn('lb-wrap', inline && 'lb-inline', className)} aria-hidden="true">
      <style>{CSS}</style>
      <svg
        className="lb-svg"
        viewBox="0 0 300 230"
        style={{
          opacity: tone === 'dark' ? 0.5 : 0.88,
          // Feathers only the OUTER edge. The previous mask was centred on the
          // comb's corner at 88% 12% and faded to nothing by 78%, which erased
          // the entire lower-left - exactly where the cookies sit and where the
          // bee spends half its flight. A motif that hides the animation is not
          // a motif, it is a bug.
          WebkitMaskImage: 'radial-gradient(135% 125% at 72% 30%, #000 62%, transparent 100%)',
          maskImage: 'radial-gradient(135% 125% at 72% 30%, #000 62%, transparent 100%)',
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* The comb: outline always, honey fill animated per cell. */}
        {CELLS.map((c, i) => (
          <path key={`c${i}`} className="lb-cell" d={hexPath(c.cx, c.cy, R)} />
        ))}
        {CELLS.map((c, i) => (
          <path key={`f${i}`} className={`lb-fill lb-fill-${i}`} d={hexPath(c.cx, c.cy, R * 0.78)} />
        ))}

        {/* The cookies waiting to be collected. */}
        {COOKIES.map((c, i) => (
          <g key={`k${i}`} transform={`translate(${c.x} ${c.y})`}>
            <Cookie className={`lb-cookie lb-cookie-${i}`} />
          </g>
        ))}

        {/* One bee per trip; exactly one is visible at a time, and each carries
            its own cookie home. Cheaper and steadier than re-targeting a single
            element's keyframes six times. */}
        {CELLS.map((c, i) => (
          <g key={`b${i}`} className={`lb-bee-g lb-bee-${i}`}>
            <Bee carry={i} />
          </g>
        ))}

        {/* The reduced-motion still: one bee parked by the full comb. */}
        <g className="lb-bee-g lb-rest" transform={`translate(${CELLS[0].cx - 74} ${CELLS[0].cy + 92})`}>
          <Bee />
        </g>
      </svg>
    </div>
  );
}

export default LoginBee;
